import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout, { PageHero } from '../../components/PublicLayout';
import { Button } from '../../components/ui/button';
import { api } from '../../lib/api';
import { ArrowRight, ArrowUpRight, ArrowDownLeft, TrendingUp, BarChart3, Globe, Briefcase, Check } from 'lucide-react';
import { useLanguage } from '../../lib/LanguageContext';

const products = [
  { id: 'pea', icon: Briefcase, name: 'PEA — Plan d’épargne en actions', desc: 'Investissez en actions européennes dans un cadre fiscal avantageux après 5 ans.', bullets: ['Plafond 150 000 €', '0 € de frais de garde', 'Fiscalité réduite > 5 ans'] },
  { id: 'cto', icon: Globe, name: 'Compte-titres ordinaire', desc: 'Accédez aux marchés mondiaux : actions US, ETF, obligations, cryptomonnaies (selon éligibilité).', bullets: ['Univers mondial', 'ETF à 0,75% de courtage', 'Cryptomonnaies'] },
  { id: 'trading', icon: BarChart3, name: 'Trading en direct', desc: 'Plateforme professionnelle avec carnets d’ordres temps réel, alertes et analyses techniques.', bullets: ['Cours en temps réel', 'Outils techniques', 'API trading'] },
];

export default function Bourse() {
  const { t } = useLanguage();
  const [stocks, setStocks] = useState([]);
  useEffect(() => { (async () => {
    try { const r = await api.get('/public/stocks'); setStocks(r.data.quotes || []); } catch (e) {}
  })(); }, []);
  return (
    <PublicLayout>
      <PageHero eyebrow={t('public.bourseEyebrow')} title={t('public.bourseTitle')} subtitle={t('public.bourseSubtitle')}>
        <Link to="/inscription"><Button size="lg" className="bg-white text-blue-900 hover:bg-slate-100">{t('public.openPea')} <ArrowRight className="ml-2 w-4 h-4"/></Button></Link>
      </PageHero>

      {/* Live indices */}
      <section id="cours" className="bg-slate-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="text-xs text-blue-700 font-semibold uppercase tracking-widest">Cours en temps réel</div>
              <h2 className="text-2xl font-bold mt-1">Indices boursiers</h2>
            </div>
            <span className="text-xs text-slate-500">Mise à jour : {new Date().toLocaleTimeString('fr-FR')}</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {stocks.length === 0 && <div className="text-slate-500 text-sm col-span-full">Chargement...</div>}
            {stocks.map((s) => (
              <div key={s.symbol} className="p-4 rounded-xl bg-white border border-slate-200">
                <div className="text-xs text-slate-500 uppercase tracking-wide font-medium">{s.name}</div>
                {s.error ? <div className="text-rose-500 text-xs mt-2">indispo.</div> : <>
                  <div className="text-lg font-bold mt-1">{s.price?.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}</div>
                  <div className={`text-xs font-semibold flex items-center gap-1 ${s.change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                    {s.change >= 0 ? <ArrowUpRight className="w-3 h-3"/> : <ArrowDownLeft className="w-3 h-3"/>}
                    {s.change >= 0 ? '+' : ''}{s.change_pct?.toFixed(2)}%
                  </div>
                </>}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-3 gap-6">
        {products.map((p) => (
          <div key={p.id} id={p.id} className="p-6 rounded-2xl border border-slate-200 bg-white hover:shadow-lg transition">
            <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center"><p.icon className="w-6 h-6"/></div>
            <h3 className="font-bold text-lg mt-4">{p.name}</h3>
            <p className="text-slate-600 text-sm mt-2">{p.desc}</p>
            <ul className="mt-3 space-y-1 text-sm">
              {p.bullets.map((b, i) => <li key={i} className="flex gap-2 items-start"><Check className="w-4 h-4 text-emerald-600 mt-0.5"/>{b}</li>)}
            </ul>
          </div>
        ))}
      </section>
      <div className="max-w-7xl mx-auto px-6 pb-16">
        <p className="text-xs text-slate-500">Investir comporte des risques de perte en capital. Les performances passées ne préjugent pas des performances futures.</p>
      </div>
    </PublicLayout>
  );
}
